import {neverland as $, html, useState} from 'neverland';
import { verifyMFA, extractGroupsFromToken, hasIdToken } from '../lib/cognito.js';
import { resolveLoginDecision } from '../lib/clusterAccess.js';
import { sendApprovalNotification } from '../lib/approval.js';

const MFAVerifyPage = $(function(auth, setAuth, theme) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function getLogo() {
    if (theme && theme.logo && theme.logo !== '') {
      return '/static/img/logos-large/' + theme.logo;
    }
    return '/static/img/transparent-placeholder.png';
  }

  function InfoTip(text) {
    return html`<span class="info-tip" title="${text}"><i class="fal fa-info-circle"></i></span>`;
  }

  async function verifyCode() {
    const code = document.getElementById('mfa-code').value.trim();
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // auth.mfaChallenge holds { type, mfaChallenge, username, session } from signIn.
      const result = await verifyMFA(auth.mfaChallenge, code);
      const groups = extractGroupsFromToken(result);

      // reg-mfa-notify fix: if this MFA completion is the tail of a REGISTRATION
      // (RegisterPage stashed registrationEntry + registrationClientGroupId into
      // auth), send the approval notification NOW — this is the first point where
      // a valid Cognito session/ID token exists (verifyMFA -> handleAuthResponse
      // -> auth.setSession on the cognito singleton), so database.js can attach
      // X-Id-Token for hop 1. Gated on the stash's PRESENCE, not the gate
      // decision, so an ORDINARY login (no stash) never sends. Fire-and-forget:
      // the helper swallows its own errors and must not block MFA completion.
      const isRegistration = !!(auth.registrationEntry && auth.registrationClientGroupId);
      if (isRegistration) {
        sendApprovalNotification(auth, auth.registrationEntry, auth.registrationClientGroupId);
      }

      // Single source of truth for the login-completion decision (shared with
      // every other login-completion site). Admins bypass; a pending user is
      // diverted to PendingApproval WITHOUT mfaVerified so no data session loads.
      const decision = await resolveLoginDecision({ email: auth.userEmail, groups, userData: auth.userData, db: auth.db, isTokenReady: hasIdToken });

      if (decision.action === 'revoked') {
        setError(decision.message);
        setLoading(false);
        return;
      } else if (decision.action === 'register') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'ConfirmOrgAccess',
          mfaChallenge: null,
          cognitoGroups: groups,
          registrationEntry: null,
          registrationClientGroupId: null
        }));
        setLoading(false);
        return;
      } else if (decision.action === 'pending') {
        setAuth(prev => ({
          ...prev,
          mfaPage: 'PendingApproval',
          mfaChallenge: null,
          cognitoGroups: groups,
          registrationEntry: null,
          registrationClientGroupId: null
        }));
        setLoading(false);
        return;
      }

      setAuth(prev => ({
        ...prev,
        mfaPage: null,
        mfaVerified: true,
        clusterAccess: decision.clusterAccess,
        mfaChallenge: null,
        userPassword: null,
        cognitoGroups: groups,
        registrationEntry: null,
        registrationClientGroupId: null
      }));
    } catch (err) {
      if (err.code === 'CodeMismatchException' || err.code === 'ExpiredCodeException' || err.code === 'NotAuthorizedException') {
        setError('That code was not correct. Make sure you enter the current code from your app (it changes every 30 seconds).');
      } else {
        setError('Invalid code. Please try again.');
      }
    }
    setLoading(false);
  }

  function handleKeyPress(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      verifyCode();
    }
  }

  function goBack() {
    setAuth(prev => ({ ...prev, mfaPage: null, mfaChallenge: null, userData: null }));
  }

  return html`
    <div class="centre-box login">
      <img src="${getLogo()}" class="squiggle logo-large" />

      <div id="login">
        <h2 style="margin-bottom: 10px; font-size: 1.2em;">Enter your code</h2>
        <p style="margin-bottom: 15px; font-size: 1em; opacity: 0.8;">
          Open your authenticator app and enter the current 6-digit code for your <strong>Reporting Portal</strong> account.
        </p>

        <span class="loginlabel">6-DIGIT CODE ${InfoTip('Open your authenticator app (e.g. Google Authenticator) and enter the 6-digit code shown for your Reporting Portal account. The code refreshes every 30 seconds. e.g. "482 931"')}</span>
        <input type="text" id="mfa-code" class="login-field" maxlength="6" inputmode="numeric" placeholder="000000" onkeyup=${handleKeyPress} autofocus />

        <button id="login-submit" onclick=${verifyCode} disabled=${loading}>
          ${loading ? 'Verifying...' : html`<i class="fal fa-lock-open"></i>&ensp;VERIFY`}
        </button>

        <button class="back-link" onclick=${goBack}>
          &larr; Back to Login
        </button>

        ${error ? html`<div class="error"><strong>ERROR</strong><p>${error}</p></div>` : ''}
      </div>
    </div>
  `;
});

export default MFAVerifyPage;
